import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { useAdminAuth } from '../context/AdminAuthContext'
import { Alert, Badge, Button, Card, ConfirmAction, Input, PageHeader, Select, Spinner, StatCard } from '../components/ui'

const initialSettings = {
  enabled: false,
  model: 'openai/gpt-oss-20b',
  apiKey: '',
  hasApiKey: false,
  apiKeyHint: null,
  maxOutputTokens: 450,
  dailyRequestLimit: 200,
  monthlyCostLimitUsd: 2,
  usage: { todayRequests: 0, monthRequests: 0, monthCostUsd: 0 },
}

const categoryLabels = {
  error: 'Erro',
  complaint: 'Reclamação',
  missing_help: 'Ajuda ausente',
  suggestion: 'Sugestão',
  other: 'Outro',
}

const statusLabels = {
  new: 'Nova',
  in_review: 'Em análise',
  resolved: 'Resolvida',
  ignored: 'Ignorada',
}

const statusTones = { new: 'danger', in_review: 'warning', resolved: 'success', ignored: 'default' }

function unwrap(response) {
  return response?.data?.data ?? response?.data
}

export default function Mavi() {
  const { isSuperAdmin } = useAdminAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [settings, setSettings] = useState(initialSettings)
  const [feedback, setFeedback] = useState([])
  const [filters, setFilters] = useState({ status: '', category: '', tenantId: '' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(location.state?.message || '')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const requests = [adminApi.listMaviFeedback(filters)]
      if (isSuperAdmin) requests.push(adminApi.getMaviSettings())
      const [feedbackResponse, settingsResponse] = await Promise.all(requests)
      setFeedback(unwrap(feedbackResponse) || [])
      if (settingsResponse) {
        setSettings((current) => ({ ...current, ...unwrap(settingsResponse), apiKey: '' }))
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar a Mavi.'))
    } finally {
      setLoading(false)
    }
  }, [filters, isSuperAdmin])

  useEffect(() => { load() }, [load])

  async function saveSettings() {
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const response = await adminApi.saveMaviSettings({
        enabled: settings.enabled,
        model: settings.model,
        apiKey: settings.apiKey || undefined,
        maxOutputTokens: Number(settings.maxOutputTokens),
        dailyRequestLimit: Number(settings.dailyRequestLimit),
        monthlyCostLimitUsd: Number(settings.monthlyCostLimitUsd),
      })
      setSettings((current) => ({ ...current, ...unwrap(response), apiKey: '' }))
      setSuccess('Configuração salva. A chave não será exibida novamente.')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a configuração.'))
    } finally {
      setSaving(false)
    }
  }

  async function removeKey() {
    setSaving(true)
    try {
      const response = await adminApi.saveMaviSettings({ ...settings, enabled: false, removeApiKey: true })
      setSettings((current) => ({ ...current, ...unwrap(response), apiKey: '' }))
      setSuccess('Chave removida e IA desativada. Os playbooks locais continuam funcionando.')
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  async function testConnection() {
    setTesting(true)
    setError('')
    setSuccess('')
    try {
      const response = await adminApi.testMaviConnection()
      setSuccess(response.data?.message || 'Conexão confirmada.')
      await load()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha no teste com o OpenRouter.'))
    } finally {
      setTesting(false)
    }
  }

  if (loading && !feedback.length) return <Spinner />

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Assistente" title="Mavi" description="Inteligência, custos e situações encaminhadas pelos usuários." icon="fa-wand-magic-sparkles" />

      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      {isSuperAdmin && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard label="Perguntas com IA hoje" value={settings.usage?.todayRequests || 0} sub={`Limite: ${settings.dailyRequestLimit}/dia`} />
            <StatCard label="Perguntas com IA no mês" value={settings.usage?.monthRequests || 0} />
            <StatCard
              label="Custo no mês"
              value={`US$ ${Number(settings.usage?.monthCostUsd || 0).toFixed(4)}`}
              sub={`Teto: US$ ${Number(settings.monthlyCostLimitUsd || 0).toFixed(2)}`}
            />
          </div>

          <Card title="OpenRouter e limites de gasto">
            <div className="space-y-4">
              <Alert type="info">
                A chave fica cifrada no servidor e nunca volta para o navegador. Saudações e respostas simples não gastam créditos.
              </Alert>
              <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })}
                />
                Usar inteligência artificial na Mavi
              </label>
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Chave da API do OpenRouter"
                  type="password"
                  autoComplete="new-password"
                  placeholder={settings.hasApiKey ? `Chave configurada ••••${settings.apiKeyHint || ''}` : 'sk-or-v1-...'}
                  value={settings.apiKey}
                  onChange={(event) => setSettings({ ...settings, apiKey: event.target.value })}
                />
                <Input
                  label="Modelo"
                  value={settings.model}
                  onChange={(event) => setSettings({ ...settings, model: event.target.value })}
                />
                <Input
                  label="Máximo de tokens por resposta"
                  type="number"
                  min="150"
                  max="900"
                  value={settings.maxOutputTokens}
                  onChange={(event) => setSettings({ ...settings, maxOutputTokens: event.target.value })}
                />
                <Input
                  label="Máximo de perguntas com IA por dia"
                  type="number"
                  min="1"
                  max="10000"
                  value={settings.dailyRequestLimit}
                  onChange={(event) => setSettings({ ...settings, dailyRequestLimit: event.target.value })}
                />
                <Input
                  label="Teto mensal em dólares"
                  type="number"
                  min="0.10"
                  max="1000"
                  step="0.10"
                  value={settings.monthlyCostLimitUsd}
                  onChange={(event) => setSettings({ ...settings, monthlyCostLimitUsd: event.target.value })}
                />
              </div>
              <p className="text-xs text-slate-500">
                Recomendado para baixo custo: openai/gpt-oss-20b. O servidor usa provedores sem coleta de dados e prioriza o menor preço.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={saveSettings} disabled={saving}>{saving ? 'Salvando...' : 'Salvar configuração'}</Button>
                <Button variant="outline" onClick={testConnection} disabled={testing || !settings.hasApiKey}>
                  {testing ? 'Testando...' : 'Testar conexão'}
                </Button>
                {settings.hasApiKey && <ConfirmAction label="Remover chave" confirmLabel="Remover e desativar" onConfirm={removeKey} disabled={saving} />}
              </div>
            </div>
          </Card>
        </>
      )}

      <Card title="Situações encaminhadas pela Mavi" action={<Button variant="outline" onClick={load}>Atualizar</Button>}>
        <div className="mb-4 grid gap-3 md:grid-cols-3">
          <Select label="Situação" value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })}>
            <option value="">Todas</option>
            {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Select>
          <Select label="Tipo" value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })}>
            <option value="">Todos</option>
            {Object.entries(categoryLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Select>
          <Input label="Base" placeholder="Ex.: technocoat" value={filters.tenantId} onChange={(event) => setFilters({ ...filters, tenantId: event.target.value })} />
        </div>

        <div className="space-y-3">
          {feedback.map((item) => (
            <button
              type="button"
              key={item.id}
              onClick={() => navigate(`/mavi/feedback/${item.id}`)}
              className="mp-card-lift w-full rounded-2xl border border-slate-200 p-4 text-left transition hover:border-blue-300 hover:bg-blue-50/30"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">MAVI-{item.id} · {item.title}</span>
                    <Badge tone={statusTones[item.status]}>{statusLabels[item.status] || item.status}</Badge>
                    <Badge tone="info">{categoryLabels[item.category] || item.category}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-slate-700">{item.summary}</p>
                  <p className="mt-2 text-xs text-slate-500">
                    Base: {item.tenant_id} {item.current_path ? `· Tela: ${item.current_path}` : ''} · Ocorrências: {item.occurrence_count}
                  </p>
                </div>
                <span className="text-xs text-slate-400">{item.updated_at}</span>
              </div>
            </button>
          ))}
          {!feedback.length && <p className="py-8 text-center text-sm text-slate-500">Nenhuma situação encontrada.</p>}
        </div>
      </Card>
    </div>
  )
}
